import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingAIAdvisorActionLink from '../../components/funding/FundingAIAdvisorActionLink';
import FundingSequenceClientSelector from '../../components/funding/FundingSequenceClientSelector';
import FundingSequenceDetailPanel from '../../components/funding/FundingSequenceDetailPanel';
import FundingSequenceFilters from '../../components/funding/FundingSequenceFilters';
import FundingSequenceGeneratePanel from '../../components/funding/FundingSequenceGeneratePanel';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingMatch } from '../../hooks/funding/useFundingMatch';
import { useFundingSequence } from '../../hooks/funding/useFundingSequence';
import { createSubmissionFromSequenceItem } from '../../services/funding/fundingSubmissionsService';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const FundingSequencePage = () => {
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [strategyType, setStrategyType] = useState('Hybrid Capital');
  const [goalAmount, setGoalAmount] = useState('');
  const [creatingSubmissionItemId, setCreatingSubmissionItemId] = useState('');
  const [sequenceStatusFilter, setSequenceStatusFilter] = useState('');

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  const agencyIdForClient = selectedClient?.agencyId || workspaceAgencyId || undefined;

  const { matches } = useFundingMatch({
    clientId: selectedClientId,
    agencyId: agencyIdForClient,
    autoLoad: Boolean(selectedClientId && agencyIdForClient),
  });

  const {
    sequences,
    activeSequence,
    loading,
    generating,
    saving,
    error,
    generate,
    createManual,
    updateItem,
    reorderItems,
    setActiveSequence,
    loadSequence,
  } = useFundingSequence({
    clientId: selectedClientId,
    agencyId: agencyIdForClient,
    autoLoad: Boolean(selectedClientId && agencyIdForClient),
  });

  const filteredSequences = useMemo(() => {
    if (!sequenceStatusFilter) return sequences;
    return sequences.filter((sequence) => sequence.status === sequenceStatusFilter);
  }, [sequences, sequenceStatusFilter]);

  const loadClients = useCallback(async () => {
    if (!isPlatformContext && !workspaceAgencyId) {
      setClients([]);
      setClientsLoading(false);
      return;
    }

    setClientsLoading(true);
    try {
      const result = await fetchFundingClients({
        agencyId: isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId,
      });
      setClients(result.clients);
      if (!selectedClientId && result.clients.length > 0) {
        setSelectedClientId(result.clients[0].id);
      }
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [isPlatformContext, workspaceAgencyId, selectedClientId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleGenerate = async () => {
    if (!selectedClientId || !agencyIdForClient) return;
    try {
      const result = await generate({
        strategyType,
        includeNotReady: false,
      });
      if (goalAmount && result?.id) {
        // goal amount can be set on next update if needed
      }
    } catch {
      // surfaced in hook
    }
  };

  const handleCreateManual = async () => {
    if (!selectedClientId || !agencyIdForClient) return;
    const name = selectedClient?.name
      ? `Application Sequence — ${selectedClient.name}`
      : 'Application Sequence';
    try {
      await createManual({
        name,
        goal_amount: goalAmount ? Number(goalAmount) : selectedClient?.requestedAmount || undefined,
        strategy_type: strategyType,
        notes: 'Manual application sequence.',
      });
    } catch {
      // surfaced in hook
    }
  };

  const handleSequenceChange = async (sequenceId) => {
    if (!sequenceId || !agencyIdForClient) return;
    const sequence = await loadSequence(sequenceId);
    if (sequence) setActiveSequence(sequence);
  };

  const handleStatusChange = async (item, status) => {
    if (!activeSequence?.id || !agencyIdForClient) return;
    await updateItem(activeSequence.id, item.id, { status });
  };

  const handleMovePhase = async (item, phase) => {
    if (!activeSequence?.id || !agencyIdForClient) return;
    const phaseItems = (activeSequence.items || []).filter((entry) => entry.phase === phase);
    await updateItem(activeSequence.id, item.id, {
      phase,
      order_index: phaseItems.length,
    });
    const updatedItems = (activeSequence.items || []).map((entry) => {
      if (entry.id === item.id) {
        return { ...entry, phase, orderIndex: phaseItems.length };
      }
      return entry;
    });
    await reorderItems(
      activeSequence.id,
      updatedItems.map((entry, index) => ({
        id: entry.id,
        phase: entry.phase,
        order_index: entry.orderIndex ?? index,
      })),
    );
  };

  const handleCreateSubmission = async (item) => {
    if (!activeSequence?.id || !agencyIdForClient) return;
    setCreatingSubmissionItemId(item.id);
    try {
      await createSubmissionFromSequenceItem(activeSequence.id, item.id, agencyIdForClient);
      await loadSequence(activeSequence.id);
    } finally {
      setCreatingSubmissionItemId('');
    }
  };

  const handleEditNotes = async (item) => {
    if (!activeSequence?.id || !agencyIdForClient) return;
    const notes = window.prompt('Sequence item notes', item.notes || '');
    if (notes == null) return;
    await updateItem(activeSequence.id, item.id, { notes });
  };

  return (
    <FundingSectionPage
      title="Application Sequence"
      subtitle="Plan the recommended order of lender and product applications by phase, documents, and potential fit."
      activeStage="sequence"
      actions={(
        <FundingAIAdvisorActionLink
          label="Review sequence strategy"
          clientId={selectedClientId || undefined}
          prompt="What should I do next?"
        />
      )}
    >
      <div className="grid gap-4 xl:grid-cols-[320px_1fr]">
        <FundingPanel title="Funding client" subtitle="Select a client to plan applications">
          <FundingSequenceClientSelector
            clients={clients}
            selectedClientId={selectedClientId}
            onSelect={setSelectedClientId}
            loading={clientsLoading}
            requiresAgencySelection={requiresAgencySelection}
          />
        </FundingPanel>

        <div className="space-y-4">
          <FundingPanel title="Sequence planner" subtitle="Generate a recommended application order from lender matches">
            <FundingSequenceGeneratePanel
              client={selectedClient}
              matchCount={matches.length}
              strategyType={strategyType}
              goalAmount={goalAmount}
              onStrategyTypeChange={setStrategyType}
              onGoalAmountChange={setGoalAmount}
              onGenerate={handleGenerate}
              onCreateManual={handleCreateManual}
              generating={generating}
              saving={saving}
            />
          </FundingPanel>

          <FundingPanel title="Sequence filters" subtitle="Switch between client sequences">
            <FundingSequenceFilters
              sequences={filteredSequences}
              activeSequenceId={activeSequence?.id}
              onSequenceChange={handleSequenceChange}
              sequenceStatus={sequenceStatusFilter}
              onSequenceStatusChange={setSequenceStatusFilter}
            />
          </FundingPanel>

          <FundingPanel title="Application board" subtitle="Recommended order grouped by phase">
            {error ? (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
                {error}
              </div>
            ) : null}
            {loading && !activeSequence ? (
              <p className="text-sm text-[var(--fg-muted)]">Loading sequences...</p>
            ) : (
              <FundingSequenceDetailPanel
                sequence={activeSequence}
                onStatusChange={handleStatusChange}
                onMovePhase={handleMovePhase}
                onEditNotes={handleEditNotes}
                onCreateSubmission={handleCreateSubmission}
                creatingSubmissionId={creatingSubmissionItemId}
                saving={saving}
              />
            )}
          </FundingPanel>
        </div>
      </div>
    </FundingSectionPage>
  );
};

export default FundingSequencePage;
